import { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Table } from '../../../components/table/index';
import { useScreenSize } from '../../../hooks/index';
import { ReverseVestingRow, ReverseVestingTermination } from '../hooks/index';
import {
  terminationEventColumnOrderMap,
  terminationEventColumnTranslationKey,
} from './reverse-vesting.types';
import { TerminationEventItem } from './termination-event-item';

export type TerminationEventProps = {
  rows: ReverseVestingRow[];
};

const hasTermination = (
  row: ReverseVestingRow,
): row is { termination: ReverseVestingTermination } & ReverseVestingRow =>
  row.termination !== null;

/**
 * Added alongside the reverse vesting table once the stakeholder is terminated — it never replaces
 * it. Rows mirror the reverse vesting table one-for-one, showing how each issuance settled.
 */
export const TerminationEvent: FC<TerminationEventProps> = ({ rows }) => {
  const { isMobile } = useScreenSize();
  const settledRows = rows.filter(hasTermination);

  if (!settledRows.length) return;

  return (
    <div className="max flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 !pt-3 lg:p-[2px]">
      <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
        <AppFormattedMessage id="TERMINATION_EVENT_REVERSE_VESTING" />
      </span>

      {isMobile ? (
        <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-3 shadow-sm">
          {settledRows.map((data) => (
            <TerminationEventItem data={data} key={data.id} />
          ))}
        </div>
      ) : (
        <div className="flex gap-2 overflow-hidden rounded-md bg-white px-2 pb-2 pt-1 shadow-sm">
          <div className="flex h-full w-full flex-col gap-1 overflow-x-auto rounded-md border border-gray-100">
            <Table
              columnOrder={terminationEventColumnOrderMap}
              columnsTitle={terminationEventColumnTranslationKey}
            >
              {settledRows.map((data) => (
                <TerminationEventItem data={data} key={data.id} mode="TABLE" />
              ))}
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
